import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { getByQuery, create } from "../../api/API";
import { toast } from "react-toastify";
import LoadingSpinner from "../Generic/LoadingSpinner";
import { getCurrentUser, isLoggedIn } from "../../utility/UserUtils";

const ReferralJobPosted = () => {
  const [referralJobs, setReferralJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(null); // job.id while sending

  useEffect(() => {
    const fetchReferralJobs = async () => {
      try {
        setJobsLoading(true);
        const data = await getByQuery(
          "referral-jobs",
          "where[status][equals]=active&sort=-createdAt&depth=2"
        );
        setReferralJobs(data.docs || []);
      } catch (err) {
        console.error("Error fetching referral jobs:", err);
        toast.error("Failed to load referral jobs.");
      } finally {
        setJobsLoading(false);
      }
    };

    fetchReferralJobs();
  }, []);

  const handleRequestReferral = async (job) => {
    if (!isLoggedIn()) {
      toast.info("Please login to send a referral request.");
      return;
    }

    const currentUser = getCurrentUser();
    if (!currentUser) {
      toast.error("User not found. Please login again.");
      return;
    }

    if (currentUser.id === job.postedBy.id) {
      toast.info("You cannot request a referral for your own job post.");
      return;
    }

    try {
      setSendingRequest(job.id);

      const body = {
        recipientEmail: job.postedBy.email,
        jobTitle: job.role || "Job Opening",
        requesterName: `${currentUser.firstName} ${currentUser.lastName}`,
        requesterEmail: currentUser.email,
        message: `Hi ${job.postedBy.firstName}, I’d like to request a referral for the ${job.role} position at ${job.company}.`,
        jobLink: job.jobLink || "",
      };

      const res = await create("send-referral-request", body);

      if (res && !res.error) {
        toast.success("Referral request email sent successfully!");
      } else {
        toast.error(res.error || "Failed to send referral request.");
      }
    } catch (error) {
      console.error("Error sending referral request:", error);
      toast.error("Something went wrong. Please try again later.");
    } finally {
      setSendingRequest(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (jobsLoading) {
    return (
      <div className="text-center my-4">
        <LoadingSpinner text="Loading jobs posted..." />
      </div>
    );
  }

  if (referralJobs.length === 0) {
    return (
      <p className="text-center text-muted mb-5">
        No active referral jobs available at the moment.
      </p>
    );
  }

  const currentUser = isLoggedIn() ? getCurrentUser() : null;

  return (
    <div className="mb-5">
      <h4 className="text-center mb-4">Referral Jobs Posted by Members</h4>
      <div className="row g-4 justify-content-center">
        {referralJobs.map((job) => {
          const isOwnPost = currentUser && currentUser.id === job.postedBy.id;

          return (
            <div key={job.id} className="col-md-4">
              <div className="card border-primary shadow-sm h-100">
                <div className="card-body d-flex flex-column">
                  {/* Job title and company */}
                  <h5 className="card-title text-primary mb-2">{job.role}</h5>
                  <p className="card-text mb-1">
                    <strong>Company:</strong> {job.company}
                  </p>
                  <p className="card-text mb-1">
                    <strong>Location:</strong> {job.location}
                  </p>
                  <p className="card-text mb-1">
                    <strong>Experience:</strong> {job.experience}
                  </p>

                  {/* Description */}
                  {job.description && (
                    <p className="card-text mb-1">
                      <strong>Description: </strong>
                      {job.description.length > 120
                        ? `${job.description.substring(0, 120)}...`
                        : job.description}
                    </p>
                  )}

                  {/* Job link */}
                  {job.jobLink && (
                    <p className="card-text small text-muted">
                      <a
                        href={job.jobLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary btn-sm mt-2 align-self-start"
                      >
                        🔗 View Job Posting
                      </a>
                    </p>
                  )}

                  {/* Footer info */}
                  {job.postedBy && (
                    <div className="mt-auto pt-3">
                      <p className="card-text mb-1">
                        <strong>Posted by:</strong>{" "}
                        {`${job.postedBy.firstName} ${job.postedBy.lastName}`}
                      </p>

                      {/* Expiry and Button in one row */}
                      <div className="d-flex justify-content-between align-items-center">
                        {job.expiryDate ? (
                          <p className="card-text mb-0 text-danger">
                            <strong>Expires On:</strong>{" "}
                            {formatDate(job.expiryDate)}
                          </p>
                        ) : (
                          <p className="card-text mb-0 text-muted">
                            No expiry date
                          </p>
                        )}

                        <Button
                          variant={isOwnPost ? "secondary" : "primary"}
                          size="sm"
                          disabled={isOwnPost || sendingRequest === job.id}
                          onClick={() => handleRequestReferral(job)}
                        >
                          {isOwnPost
                            ? "Your Post"
                            : sendingRequest === job.id
                            ? "Sending..."
                            : "Request Referral"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReferralJobPosted;
